import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  FormField,
  SpaceBetween,
  Form,
  Container,
  InputProps,
} from "@cloudscape-design/components-themed";
import { Auth } from "aws-amplify";
import PasswordPolicy from "../../features/authentication/PasswordPolicy";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { COGNITO_ERR_MSG } from "../../features/authentication/constants";
import { useAppSelector, useAppDispatch } from "../../app/hook";
import { forgetLocation } from "../../features/authentication/location-slice";
import { addNotification } from "../../features/error/notifications-slice";
import {
  createFormFieldErrTxts,
  clearFormFieldErrTxt,
  clearAllErrTxts,
} from "../../features/error/validation-slice";
import {
  isPasswordPolicyValid,
  getErrorText,
} from "../../utils/inputValidation";
import { Messages } from "../../features/error/messages";

interface UserInput {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const Password = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { formFieldErrTxts } = useAppSelector((state) => state.validation);

  // URL取得
  const pathname = useLocation().pathname;

  // 画面遷移する際にエラーメッセージの初期化
  useEffect(() => {
    dispatch(clearAllErrTxts());
  }, [dispatch, pathname]);

  //ユーザ入力
  const [input, setInput] = useState<UserInput>({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  /**
   * 入力チェック
   * @param name
   * @returns
   */
  const checkInput = (name: keyof UserInput, value: string): boolean => {
    switch (name) {
      case "oldPassword":
        if (!value) {
          dispatch(
            createFormFieldErrTxts({
              name: "password",
              msgId: "KGRERR023",
              msgArgs: [LABELS.OLD_PW],
            })
          );
          return false;
        } else {
          dispatch(clearFormFieldErrTxt("password"));
          return true;
        }
      case "newPassword":
        if (!value) {
          dispatch(
            createFormFieldErrTxts({
              name: "newPassword",
              msgId: "KGRERR023",
              msgArgs: [LABELS.NEW_PW],
            })
          );
          return false;
        } else if (!isPasswordPolicyValid(value)) {
          dispatch(
            createFormFieldErrTxts({
              name: "newPassword",
              msgId: "KGRERR043",
              msgArgs: [],
            })
          );
          return false;
        } else {
          dispatch(clearFormFieldErrTxt("newPassword"));
          return true;
        }
      case "confirmNewPassword":
        if (!value) {
          dispatch(
            createFormFieldErrTxts({
              name: "confirmNewPassword",
              msgId: "KGRERR023",
              msgArgs: [LABELS.CONFIRM_PW],
            })
          );
          return false;
        } else {
          dispatch(clearFormFieldErrTxt("confirmNewPassword"));
          return true;
        }
      default:
        return false;
    }
  };

  /**
   * バリデーションチェック
   * @returns
   */
  const validationCheck = (): boolean => {
    const oldPassCheckResult = checkInput("oldPassword", input.oldPassword);
    const newPassCheckResult = checkInput("newPassword", input.newPassword);
    const confirmNewPassCheckResult = checkInput(
      "confirmNewPassword",
      input.confirmNewPassword
    );
    return (
      oldPassCheckResult && newPassCheckResult && confirmNewPassCheckResult
    );
  };

  // 登録更新関数
  const handleInputOnChange = (name: keyof UserInput, value: string) => {
    setInput({ ...input, [name]: value });
    checkInput(name, value);
  };

  /**
   * エラー処理
   * @param err
   */
  const handleErr = (err: Error) => {
    let msgId: keyof Messages;
    let msgArgs: string[];
    switch (err.message) {
      case COGNITO_ERR_MSG.wrongPassword:
        msgId = "KGRERR046";
        msgArgs = [];
        break;
      case COGNITO_ERR_MSG.resetBlocked:
        msgId = "KGRERR045";
        msgArgs = [];
        break;
      default:
        if (
          err.message.includes(COGNITO_ERR_MSG.passwordPolicyDidNotSatisfied)
        ) {
          msgId = "KGRERR043";
          msgArgs = [];
        } else {
          msgId = "KGRFAT001";
          msgArgs = [];
        }
    }
    dispatch(
      addNotification({
        msgId: msgId,
        msgArgs: msgArgs,
      })
    );
  };

  /**
   * パスワード変更関数
   */
  const handleOnSubmitClick = async () => {
    if (validationCheck()) {
      if (input.newPassword !== input.confirmNewPassword) {
        dispatch(
          addNotification({
            msgId: "KGRERR042",
            msgArgs: [],
          })
        );
      } else {
        await Auth.currentAuthenticatedUser()
          .then((user) => {
            return Auth.changePassword(
              user,
              input.oldPassword,
              input.newPassword
            );
          })
          .then((res) => {
            return Auth.signOut();
          })
          .then((res) => {
            dispatch(forgetLocation());
            navigate("/sign-in");
          })
          .catch((err) => {
            handleErr(err);
          });
      }
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={handleOnSubmitClick}>
              {COMMON_LABELS.BTNS.UPDATE}
            </Button>
          </SpaceBetween>
        }
      >
        <Container>
          <SpaceBetween direction="vertical" size="xs">
            <FormField
              label={LABELS.OLD_PW}
              errorText={getErrorText("password", formFieldErrTxts)}
            >
              <Input
                name="oldPassword"
                value={input.oldPassword}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleInputOnChange("oldPassword", detail.value)
                }
                type="password"
              />
            </FormField>
            <FormField
              label={LABELS.NEW_PW}
              errorText={getErrorText("newPassword", formFieldErrTxts)}
            >
              <Input
                name="newPassword"
                value={input.newPassword}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleInputOnChange("newPassword", detail.value)
                }
                type="password"
              />
            </FormField>
            <FormField
              label={LABELS.CONFIRM_PW}
              errorText={getErrorText("confirmNewPassword", formFieldErrTxts)}
            >
              <Input
                name="confirmNewPassword"
                value={input.confirmNewPassword}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleInputOnChange("confirmNewPassword", detail.value)
                }
                type="password"
              />
            </FormField>
            <PasswordPolicy />
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
};

export default Password;
